/**
 * UIErrorDisplayProvider - Manages error display state (toast/modal)
 * Implements IUIErrorDisplay interface from ErrorHandler
 * Renders ErrorToast and ErrorModal based on current state
 */

import React, { createContext, useCallback, useState } from "react";
import { ErrorToast } from "../ui/ErrorToast";
import { ErrorModal } from "../ui/ErrorModal";
import { ErrorSeverity } from "@akfdev-com/shared";

interface ToastState {
  message: string;
  errorId: string;
  severity?: ErrorSeverity;
  actions?: Array<{ label: string; onClick: () => void }>;
}

interface ModalState {
  title: string;
  message: string;
  footer: string;
  buttons: Array<{ label: string; onClick?: () => void }>;
}

interface UIErrorDisplayContextType {
  showToast: (options: {
    message: string;
    errorId: string;
    severity?: string;
    actions?: Array<{ label: string; onClick: () => void }>;
  }) => void;
  showModal: (options: {
    title: string;
    message: string;
    footer: string;
    buttons: Array<{ label: string; onClick?: () => void }>;
  }) => void;
}

export const UIErrorDisplayContext = createContext<
  UIErrorDisplayContextType | undefined
>(undefined);

interface UIErrorDisplayProviderProps {
  children: React.ReactNode;
}

export const UIErrorDisplayProvider: React.FC<UIErrorDisplayProviderProps> = ({
  children,
}) => {
  const [toastState, setToastState] = useState<ToastState | null>(null);
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const showToast = useCallback(
    (options: {
      message: string;
      errorId: string;
      severity?: string;
      actions?: Array<{ label: string; onClick: () => void }>;
    }) => {
      setToastState({
        message: options.message,
        errorId: options.errorId,
        severity: (options.severity as ErrorSeverity) || "ERROR",
        actions: options.actions,
      });
    },
    [],
  );

  const showModal = useCallback(
    (options: {
      title: string;
      message: string;
      footer: string;
      buttons: Array<{ label: string; onClick?: () => void }>;
    }) => {
      setModalState({
        title: options.title,
        message: options.message,
        footer: options.footer,
        buttons: options.buttons,
      });
    },
    [],
  );

  const handleToastDismiss = useCallback(() => {
    setToastState(null);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalState(null);
  }, []);

  const contextValue: UIErrorDisplayContextType = {
    showToast,
    showModal,
  };

  return (
    <UIErrorDisplayContext.Provider value={contextValue}>
      {children}
      {toastState && (
        <ErrorToast
          message={toastState.message}
          errorId={toastState.errorId}
          severity={toastState.severity}
          actions={toastState.actions}
          onDismiss={handleToastDismiss}
        />
      )}
      {modalState && (
        <ErrorModal
          title={modalState.title}
          message={modalState.message}
          footer={modalState.footer}
          buttons={modalState.buttons}
          onClose={handleModalClose}
        />
      )}
    </UIErrorDisplayContext.Provider>
  );
};

UIErrorDisplayProvider.displayName = "UIErrorDisplayProvider";
